/**
 * Liquid-metal animated border for the search inputs.
 *
 * Mounts the @paper-design/shaders "liquid metal" WebGL shader (imported from
 * esm.sh at runtime) into `[data-liquid-metal-border]` host elements. Each
 * host is CSS-masked down to a thin ring (see the `lm-ring` styles in
 * sections/header.liquid and snippets/search-modal.liquid), so the shader
 * reads as an animated metallic border around the search input.
 *
 * Design notes:
 * - Lazy by visibility: the esm.sh import + WebGL canvas are only created
 *   once a host is actually visible (IntersectionObserver). The search modal
 *   is display:none until opened, so nothing loads/renders until then; the
 *   desktop inline bar initializes after first paint (idle callback).
 * - ShaderMount installs its own ResizeObserver on the host element, so the
 *   canvas re-fits automatically on resize/orientation changes.
 * - Defensive: failures (CSP blocking esm.sh, no WebGL2, offline) degrade
 *   silently. The `lm-border-active` class is only added on success, so the
 *   theme's original static borders remain as the fallback.
 */

const HOST_SELECTOR = '[data-liquid-metal-border]';
const SHADER_LIBRARY_URL = 'https://esm.sh/@paper-design/shaders';
const ACTIVE_CLASS = 'lm-border-active';
// Slow, subtle drift — the demo's 0.6 reads as frantic on a thin border.
const DEFAULT_SPEED = 0.18;

/**
 * Tuned for a MINIMAL, PROFESSIONAL look (not the flashy demo defaults):
 * - u_shiftRed / u_shiftBlue: 0 — these created the iridescent "rainbow"
 *   chromatic separation. Zeroed out, the metal reads as a clean monochrome
 *   chrome that only carries the purple tint below.
 * - u_repetition lowered + u_softness raised + u_scale raised — fewer, softer,
 *   larger bands instead of a busy high-frequency shimmer.
 * - u_shape: 0 = "full-fill on canvas" so the metal covers the entire canvas
 *   and the masked ring shimmers along its whole length (the demo's shape 1
 *   is a centered circle, which would leave the ring edges empty).
 * - u_colorTint: brand purple (#7c3aed) applied as a partial color-burn so
 *   the chrome leans an elegant purple instead of pure silver.
 */
const DEFAULT_UNIFORMS = {
  u_repetition: 1.0,
  u_softness: 0.85,
  u_shiftRed: 0.0,
  u_shiftBlue: 0.0,
  u_distortion: 0,
  u_contour: 0,
  u_angle: 100,
  u_scale: 2.2,
  u_shape: 0,
  u_offsetX: 0.1,
  u_offsetY: -0.1,
  u_colorTint: [0.486, 0.227, 0.929, 0.5],
};

const SEARCH_CIRCLE_UNIFORMS = {
  u_repetition: 1.5,
  u_softness: 0.5,
  u_shiftRed: 0.3,
  u_shiftBlue: 0.3,
  u_distortion: 0,
  u_contour: 0,
  u_angle: 100,
  u_scale: 1.5,
  u_shape: 1,
  u_offsetX: 0.1,
  u_offsetY: -0.1,
};

const PRESET_UNIFORMS = {
  default: DEFAULT_UNIFORMS,
  'search-circle': SEARCH_CIRCLE_UNIFORMS,
};

let libraryPromise = null;

function loadLibrary() {
  if (!libraryPromise) {
    libraryPromise = import(SHADER_LIBRARY_URL);
  }
  return libraryPromise;
}

/**
 * @param {Record<string, unknown>} uniforms
 */
function cloneUniforms(uniforms) {
  return Object.fromEntries(
    Object.entries(uniforms).map(([key, value]) => [key, Array.isArray(value) ? [...value] : value])
  );
}

/**
 * @param {HTMLElement} host
 */
function getHostConfig(host) {
  const preset = host.dataset.liquidMetalPreset || 'default';
  const presetUniforms = PRESET_UNIFORMS[preset] || DEFAULT_UNIFORMS;
  const speedValue = Number(host.dataset.liquidMetalSpeed);

  return {
    speed: Number.isFinite(speedValue) ? speedValue : DEFAULT_SPEED,
    uniforms: cloneUniforms(presetUniforms),
  };
}

/**
 * @param {HTMLElement} host
 */
async function mountShader(host) {
  if (host.dataset.lmMounted) return;
  host.dataset.lmMounted = 'true';

  try {
    const { ShaderMount, liquidMetalFragmentShader } = await loadLibrary();

    if (!host.isConnected || host.paperShaderMount) return;

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    const { speed, uniforms } = getHostConfig(host);

    // Same call shape as the reference demo: (element, shader, uniforms, webGlContextAttributes, speed)
    new ShaderMount(host, liquidMetalFragmentShader, uniforms, undefined, reducedMotion ? 0 : speed);

    // Reveal the ring / hide the static fallback border on the wrapper.
    host.parentElement?.classList.add(ACTIVE_CLASS);
  } catch (error) {
    // Allow a retry on the next visibility trigger (e.g. transient network failure).
    delete host.dataset.lmMounted;
    libraryPromise = null;
    console.warn('[liquid-metal-border] Shader unavailable, keeping static border.', error);
  }
}

const observer = new IntersectionObserver((entries) => {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    // Skip zero-size targets (e.g. hidden drawer markup mid-transition).
    if (entry.boundingClientRect.width < 1 || entry.boundingClientRect.height < 1) continue;

    observer.unobserve(entry.target);
    mountShader(/** @type {HTMLElement} */ (entry.target));
  }
});

/**
 * @param {ParentNode} [root]
 */
function discover(root) {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;

  for (const host of scope.querySelectorAll(HOST_SELECTOR)) {
    if (host.dataset.lmObserved) continue;
    host.dataset.lmObserved = 'true';
    observer.observe(host);
  }
}

function start() {
  const schedule = window.requestIdleCallback ?? ((callback) => window.setTimeout(callback, 150));
  schedule(() => discover(document));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}

// Theme editor: sections re-render and recreate the search markup.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) discover(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof Element)) return;
  for (const host of event.target.querySelectorAll(HOST_SELECTOR)) {
    host.paperShaderMount?.dispose?.();
  }
});
